import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Moon, Mic, Loader2, Play, Pause, LogOut, Trash2, Volume2, VolumeX, Radio, MessageCircle, X, Save, Zap, Users, Share2, Flame, Copy, Check, Bell, Code2, Crown, Globe } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";
import { ASTRO_LANGS, tAstro, getStoredAstroLang, setStoredAstroLang } from "@/lib/astroI18n";

// ────────────────────────────────────────────────────────────
// Language context — one source of truth for the whole /astro tree.
// ────────────────────────────────────────────────────────────
const AstroLangCtx = React.createContext({ lang: "en", setLang: () => {} });
function useLang() {
  const ctx = React.useContext(AstroLangCtx);
  return { lang: ctx.lang, setLang: ctx.setLang, t: (k) => tAstro(k, ctx.lang) };
}
function AstroLangProvider({ children }) {
  const [lang, setLang] = React.useState(() => getStoredAstroLang());
  React.useEffect(() => { setStoredAstroLang(lang); }, [lang]);
  return <AstroLangCtx.Provider value={{ lang, setLang }}>{children}</AstroLangCtx.Provider>;
}

/**
 * Fliboe Astro — audio-first astrology PWA. Isolated route tree at /astro/*,
 * own login and localStorage token (`fliboe_astro_token`) so no state bleeds
 * to FM / Flip / Casino. Reuses main /api/auth/* + a fresh /api/astro/*
 * surface. Ambient bed music is a Web-Audio drone (zero-asset MVP); when the
 * admin uploads a real MP3 via /admin/astro/bed-track the frontend will
 * prefer that file instead.
 */
const ASTRO_TOKEN_KEY = "fliboe_astro_token";

function astroAuthHeaders() {
  const t = localStorage.getItem(ASTRO_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ────────────────────────────────────────────────────────────
// Zero-asset ambient bed — slow sine drone in C, plays on click only
// so autoplay policies stay happy. Replaced by uploaded MP3 when present.
// ────────────────────────────────────────────────────────────
function startAmbientDrone(ref) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const master = ctx.createGain(); master.gain.value = 0.0; master.connect(ctx.destination);
    // Slow attack up to 0.08 over 3s
    master.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 3.0);
    const notes = [130.81, 196.0, 261.63, 329.63]; // C3 G3 C4 E4 — open cosmic pad
    const oscs = notes.map((f, i) => {
      const o = ctx.createOscillator(); o.type = i % 2 === 0 ? "sine" : "triangle"; o.frequency.value = f;
      const g = ctx.createGain(); g.gain.value = 0.25 - i * 0.05;
      // Slow LFO breathing
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08 + i * 0.02;
      const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.05;
      lfo.connect(lfoGain).connect(g.gain); lfo.start();
      o.connect(g).connect(master); o.start();
      return { o, lfo };
    });
    ref.current = { ctx, master, oscs };
  } catch (_) {}
}
function stopAmbientDrone(ref) {
  const s = ref.current; if (!s) return;
  try {
    s.master.gain.linearRampToValueAtTime(0, s.ctx.currentTime + 1.2);
    setTimeout(() => { try { s.oscs.forEach(x => { x.o.stop(); x.lfo.stop(); }); s.ctx.close(); } catch (_) {} }, 1400);
  } catch (_) {}
  ref.current = null;
}

// ────────────────────────────────────────────────────────────
// Shared header + starfield background
// ────────────────────────────────────────────────────────────
function Starfield() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(126,74,255,0.25),transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(255,143,230,0.15),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.35]" style={{
        backgroundImage: "radial-gradient(1px 1px at 20% 30%,#fff,transparent 40%),radial-gradient(1px 1px at 80% 15%,#c7c0ff,transparent 40%),radial-gradient(1.5px 1.5px at 55% 65%,#fff,transparent 40%),radial-gradient(1px 1px at 40% 80%,#e0b6ff,transparent 40%),radial-gradient(1px 1px at 10% 60%,#fff,transparent 40%),radial-gradient(1.5px 1.5px at 90% 55%,#fff,transparent 40%)",
        backgroundSize: "600px 600px",
      }} />
    </div>
  );
}
function LanguagePicker() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const current = ASTRO_LANGS.find(l => l.code === lang) || ASTRO_LANGS[0];
  return (
    <div className="relative" data-testid="astro-lang-picker">
      <button
        onClick={() => setOpen(o => !o)}
        className="fl-btn ghost !py-1.5 !px-3 text-xs !gap-1.5"
        aria-label={t("shell.language")}
        data-testid="astro-lang-btn"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{current.native}</span>
        <span className="sm:hidden uppercase">{current.code}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 max-h-80 overflow-auto rounded-xl border border-white/10 bg-[#0c0724] shadow-2xl z-30 py-1" data-testid="astro-lang-menu">
            {ASTRO_LANGS.map(l => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-white/5 ${l.code === lang ? "text-violet-300" : "text-white/80"}`}
                data-testid={`astro-lang-${l.code}`}
              >
                <span>{l.native}</span>
                <span className="text-white/40 text-[10px] fl-mono uppercase tracking-widest">{l.code}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AstroHeader({ signedIn = false, onSignOut }) {
  const { t } = useLang();
  return (
    <header className="relative z-10 border-b border-white/5">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/astro" className="flex items-center gap-2 group" data-testid="astro-brand">
          <Sparkles className="w-5 h-5 text-violet-300 group-hover:text-violet-200" />
          <span className="fl-display text-lg font-black tracking-tight text-white">Fliboe <span className="text-violet-300">Astro</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguagePicker />
          {signedIn ? (
            <button onClick={onSignOut} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="astro-signout"><LogOut className="w-3.5 h-3.5"/> {t("shell.signOut")}</button>
          ) : (
            <Link to="/astro/login" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="astro-header-signin">{t("shell.signIn")}</Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ────────────────────────────────────────────────────────────
// LANDING (/astro)
// ────────────────────────────────────────────────────────────
function AstroLandingInner() {
  const { t } = useLang();
  return (
    <main className="min-h-screen bg-[#070417] text-white relative" data-testid="astro-landing">
      <Starfield />
      <div className="relative z-10">
        <AstroHeader />
        <section className="max-w-5xl mx-auto px-5 pt-16 pb-24 sm:pt-24">
          <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-violet-300 mb-4">// {t("landing.eyebrow")}</div>
          <h1 className="fl-display text-5xl sm:text-7xl font-black tracking-tighter leading-[0.9]">
            {t("landing.h1a")}<br />
            <span className="text-violet-300">{t("landing.h1b")}</span> {t("landing.h1c")}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">{t("landing.lede")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/astro/reading" className="fl-btn" data-testid="astro-landing-start"><Play className="w-4 h-4" /> {t("landing.cta.start")}</Link>
            <Link to="/astro/login" className="fl-btn ghost" data-testid="astro-landing-signin">{t("shell.signIn")}</Link>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: Radio,         title: t("landing.card1.h"), body: t("landing.card1.b") },
              { icon: Mic,           title: t("landing.card2.h"), body: t("landing.card2.b") },
              { icon: Moon,          title: t("landing.card3.h"), body: t("landing.card3.b") },
            ].map((f, i) => (
              <div key={i} className="fl-card p-5 bg-white/[0.02] border-white/5">
                <f.icon className="w-5 h-5 text-violet-300" />
                <h3 className="mt-3 fl-display text-lg font-bold text-white">{f.title}</h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-xs fl-mono uppercase tracking-widest text-white/40">{t("landing.pricing")}</div>
        </section>
      </div>
    </main>
  );
}
export function AstroLanding() { return <AstroLangProvider><AstroLandingInner /></AstroLangProvider>; }

// ────────────────────────────────────────────────────────────
// LOGIN (/astro/login) — reuses main /auth/* endpoints
// ────────────────────────────────────────────────────────────
export function AstroLogin() { return <AstroLangProvider><AstroLoginInner /></AstroLangProvider>; }
function AstroLoginInner() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.(); setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try { await api.post("/auth/forgot-password", { email }); setForgotOk(true); }
      catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!email.trim() || password.length < 6) { setErr("Email + 6-char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup" ? { email, password, name } : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token");
      localStorage.setItem(ASTRO_TOKEN_KEY, data.session_token);
      navigate("/astro/reading");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Sign-in failed."); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#070417] text-white relative" data-testid="astro-login-page">
      <Starfield />
      <div className="relative z-10">
        <AstroHeader />
        <section className="max-w-md mx-auto px-5 py-16">
          <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-violet-300 mb-3 text-center">// Fliboe Astro</div>
          <h1 className="fl-display text-3xl font-black text-center">
            {mode === "signup" ? "Create your Astro account" : mode === "forgot" ? "Reset your password" : "Welcome back, star-gazer"}
          </h1>
          <form onSubmit={submit} className="fl-card p-6 mt-6 space-y-3 bg-black/60 border-violet-400/20">
            {mode === "forgot" && forgotOk ? (
              <div className="text-emerald-300 text-sm text-center py-4" data-testid="astro-forgot-ok">
                If <strong>{email}</strong> matches an account, we&apos;ve emailed a reset link.
                <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-violet-300 text-xs hover:underline" data-testid="astro-forgot-back">Back to sign in</button>
              </div>
            ) : (
              <>
                {mode === "signup" && (
                  <input className="fl-input w-full" placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="astro-login-name" />
                )}
                <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="astro-login-email" />
                {mode !== "forgot" && (
                  <input className="fl-input w-full" placeholder="Password (6+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="astro-login-password" />
                )}
                {err && <div className="text-red-400 text-sm" data-testid="astro-login-err">{err}</div>}
                <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-violet-500 hover:!bg-violet-400" data-testid="astro-login-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {mode === "signup" ? "Create & begin" : mode === "forgot" ? "Send reset email" : "Sign in"}
                </button>
                {mode !== "forgot" && (
                  <>
                    <div className="flex items-center gap-3 my-1">
                      <div className="flex-1 h-px bg-white/10" />
                      <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const redirectUrl = window.location.origin + "/astro/auth-callback";
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                      }}
                      className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition"
                      data-testid="astro-login-google"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                      Continue with Google
                    </button>
                  </>
                )}
                <div className="flex items-center justify-between gap-3 mt-2">
                  <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }} className="text-violet-300 text-xs hover:underline" data-testid="astro-login-toggle">
                    {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
                  </button>
                  {mode !== "forgot" && (
                    <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-violet-300 hover:underline" data-testid="astro-login-forgot">
                      Forgot password?
                    </button>
                  )}
                </div>
              </>
            )}
          </form>
        </section>
      </div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// GOOGLE AUTH CALLBACK (/astro/auth-callback)
// ────────────────────────────────────────────────────────────
export function AstroAuthCallback() { return <AstroLangProvider><AstroAuthCallbackInner /></AstroLangProvider>; }
function AstroAuthCallbackInner() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return; processed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) { navigate("/astro/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("No token");
        localStorage.setItem(ASTRO_TOKEN_KEY, data.session_token);
        window.history.replaceState({}, document.title, "/astro/reading");
        navigate("/astro/reading", { replace: true });
      } catch (_) { navigate("/astro/login", { replace: true }); }
    })();
  }, [navigate]);
  return (
    <main className="min-h-screen bg-[#070417] text-white grid place-items-center relative" data-testid="astro-auth-callback">
      <Starfield />
      <div className="relative text-violet-300 fl-mono text-xs uppercase tracking-widest">// Signing you in…</div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// MAIN APP (/astro/reading)
// ────────────────────────────────────────────────────────────
export function AstroApp() { return <AstroLangProvider><AstroAppInner /></AstroLangProvider>; }
function AstroAppInner() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [bedTrackUrl, setBedTrackUrl] = useState(null);

  const signOut = () => { localStorage.removeItem(ASTRO_TOKEN_KEY); navigate("/astro"); };

  const loadMe = async () => {
    try {
      const { data } = await api.get("/astro/me", { headers: astroAuthHeaders() });
      setMe(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/astro/login");
      else setErr(e?.response?.data?.detail || "Could not load your account.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem(ASTRO_TOKEN_KEY)) { navigate("/astro/login"); return; }
    loadMe();
    api.get("/astro/bed-track").then(({ data }) => setBedTrackUrl(data?.audio_url || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user changes language, persist it on their chart so all subsequent
  // AI content (broadcast, oracle, alibi, synastry, believer) is generated in
  // the chosen language. No-op until the chart exists.
  useEffect(() => {
    if (!me?.chart || !lang) return;
    if ((me.chart.lang || "en") === lang) return;
    api.post("/astro/chart", {
      birth_date: me.chart.birth_date,
      birth_time: me.chart.birth_time || null,
      birth_place: me.chart.birth_place || null,
      display_name: me.chart.display_name || null,
      lang,
    }, { headers: astroAuthHeaders() }).then(() => loadMe()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, me?.chart?.lang]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#070417] text-violet-300 grid place-items-center fl-mono text-xs uppercase tracking-widest" data-testid="astro-loading">
        <Starfield />// {t("shell.loading")}
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#070417] text-white relative" data-testid="astro-app">
      <Starfield />
      <div className="relative z-10">
        <AstroHeader signedIn onSignOut={signOut} />
        <section className="max-w-4xl mx-auto px-5 py-8 space-y-8">
          {err && <div className="fl-card p-4 border-red-500/40 bg-red-950/40 text-red-200 text-sm" data-testid="astro-err">{err}</div>}
          {!me?.chart ? (
            <BirthChartForm onSaved={loadMe} />
          ) : (
            <>
              <ChartSummary chart={me.chart} isPro={me.is_pro} quota={me.quota} onEdit={loadMe} />
              <PulseAlerts sign={me.chart.sign} />
              <CosmicRadio bedTrackUrl={bedTrackUrl} onGenerated={loadMe} />
              <BelieverShow isBeliever={!!me.is_believer} />
              <CosmicAlibi sign={me.chart.sign} onUsed={loadMe} />
              <IsItJustMe sign={me.chart.sign} />
              <MicroSynastry sign={me.chart.sign} onUsed={loadMe} />
              <TheOracle personas={me.personas} bedTrackUrl={bedTrackUrl} onDone={loadMe} />
              <ReadingsArchive />
              <EmbedSnippet defaultSign={me.chart.sign} />
            </>
          )}
        </section>
      </div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// Birth chart form (first-run)
// ────────────────────────────────────────────────────────────
function BirthChartForm({ onSaved, existing }) {
  const { t, lang } = useLang();
  const [name, setName] = useState(existing?.display_name || "");
  const [date, setDate] = useState(existing?.birth_date || "");
  const [time, setTime] = useState(existing?.birth_time || "");
  const [place, setPlace] = useState(existing?.birth_place || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e?.preventDefault?.(); setErr("");
    if (!date) { setErr(t("chart.date")); return; }
    setBusy(true);
    try {
      await api.post("/astro/chart", { birth_date: date, birth_time: time || null, birth_place: place || null, display_name: name || null, lang }, { headers: astroAuthHeaders() });
      onSaved?.();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="fl-card p-6 bg-white/[0.02] border-violet-400/20" data-testid="astro-chart-form">
      <div className="fl-mono text-[11px] tracking-widest uppercase text-violet-300">// {t("chart.eyebrow")}</div>
      <h2 className="fl-display text-2xl font-black text-white mt-2">{t("chart.title")}</h2>
      <p className="text-slate-400 text-sm mt-2">{t("chart.subtitle")}</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">{t("chart.name")}</span>
          <input className="fl-input w-full mt-1" value={name} onChange={e => setName(e.target.value)} data-testid="astro-name" />
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">{t("chart.date")}</span>
          <input type="date" className="fl-input w-full mt-1" value={date} onChange={e => setDate(e.target.value)} required data-testid="astro-birth-date" />
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">{t("chart.time")}</span>
          <input type="time" className="fl-input w-full mt-1" value={time} onChange={e => setTime(e.target.value)} data-testid="astro-birth-time" />
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">{t("chart.place")}</span>
          <input className="fl-input w-full mt-1" value={place} onChange={e => setPlace(e.target.value)} data-testid="astro-birth-place" />
        </label>
      </div>
      {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-chart-err">{err}</div>}
      <button type="submit" disabled={busy} className="fl-btn mt-5" data-testid="astro-chart-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("chart.save")}
      </button>
    </form>
  );
}

// ────────────────────────────────────────────────────────────
// Chart summary card (once saved)
// ────────────────────────────────────────────────────────────
function ChartSummary({ chart, isPro, quota, onEdit }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <BirthChartForm existing={chart} onSaved={() => { setEditing(false); onEdit?.(); }} />;
  return (
    <div className="fl-card p-5 bg-white/[0.02] border-violet-400/20 flex flex-wrap items-center gap-5" data-testid="astro-chart-summary">
      <div className="text-6xl select-none" aria-hidden>{chart.glyph}</div>
      <div className="flex-1 min-w-[180px]">
        <div className="fl-mono text-[10px] uppercase tracking-widest text-violet-300">// {chart.element} · {chart.archetype}</div>
        <h2 className="fl-display text-2xl font-black text-white capitalize mt-1">{chart.display_name}, you are a {chart.sign}.</h2>
        <div className="text-slate-400 text-xs mt-1">Born {chart.birth_date}{chart.birth_time ? ` at ${chart.birth_time}` : ""}{chart.birth_place ? ` · ${chart.birth_place}` : ""}</div>
        {(chart.moon || chart.rising) && (
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] fl-mono uppercase tracking-widest" data-testid="astro-full-chart">
            {chart.moon && (
              <span className="px-2 py-1 rounded-full bg-violet-500/15 text-violet-200 border border-violet-400/20">
                <span aria-hidden className="mr-1">{chart.moon.glyph}</span>Moon · <span className="capitalize">{chart.moon.sign}</span>
              </span>
            )}
            {chart.rising && (
              <span className="px-2 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/20">
                <span aria-hidden className="mr-1">{chart.rising.glyph}</span>Rising · <span className="capitalize">{chart.rising.sign}</span>
              </span>
            )}
            {(chart.moon?.approx || chart.rising?.approx) && (
              <span className="text-white/40 lowercase tracking-normal font-mono">approx · save birth time for a sharper rising</span>
            )}
          </div>
        )}
        {!isPro && !chart.moon && (
          <div className="mt-2 text-[11px] text-emerald-300/80 fl-mono uppercase tracking-widest" data-testid="astro-fullchart-upsell">// Unlock moon + rising with Astro Pro</div>
        )}
      </div>
      <div className="text-right">
        <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50">Today&apos;s quota</div>
        <div className="text-white text-sm mt-1">
          {isPro ? "Unlimited" : (
            <>
              Broadcast <span className="text-violet-300">{quota.broadcasts_used}/{quota.broadcasts_limit}</span>{" · "}
              Oracle <span className="text-violet-300">{quota.oracle_used}/{quota.oracle_limit}</span>
            </>
          )}
        </div>
        <button onClick={() => setEditing(true)} className="mt-2 text-xs text-violet-300 hover:underline" data-testid="astro-chart-edit">Edit chart</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Cosmic Radio — daily broadcast player
// ────────────────────────────────────────────────────────────
function CosmicRadio({ bedTrackUrl, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [broadcast, setBroadcast] = useState(null);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const [bedOn, setBedOn] = useState(true);
  const audioRef = useRef(null);
  const bedRef = useRef(null);
  const bedAudioRef = useRef(null);

  useEffect(() => {
    api.get("/astro/broadcast/today", { headers: astroAuthHeaders() })
      .then(({ data }) => setBroadcast(data?.audio_url ? data : null))
      .catch(() => {});
    return () => { try { audioRef.current?.pause(); } catch (_) {} stopAmbientDrone(bedRef); try { bedAudioRef.current?.pause(); } catch (_) {} };
  }, []);

  const generate = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/astro/broadcast/generate", {}, { headers: astroAuthHeaders(), timeout: 180000 });
      setBroadcast(data); onGenerated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "The stars are taking their time — try again." : "Broadcast generation failed."));
    } finally { setBusy(false); }
  };

  const toggleBed = () => {
    if (bedOn) {
      stopAmbientDrone(bedRef);
      try { bedAudioRef.current?.pause(); } catch (_) {}
      setBedOn(false);
    } else {
      if (bedTrackUrl) {
        const a = new Audio(resolveAudio(bedTrackUrl)); a.loop = true; a.volume = 0.35;
        a.play().catch(() => {}); bedAudioRef.current = a;
      } else { startAmbientDrone(bedRef); }
      setBedOn(true);
    }
  };

  const play = () => {
    if (!broadcast?.audio_url) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (bedOn) {
      if (bedTrackUrl && !bedAudioRef.current) {
        const a = new Audio(resolveAudio(bedTrackUrl)); a.loop = true; a.volume = 0.35;
        a.play().catch(() => {}); bedAudioRef.current = a;
      } else if (!bedTrackUrl && !bedRef.current) {
        startAmbientDrone(bedRef);
      }
    }
    if (!audioRef.current) {
      const a = new Audio(resolveAudio(broadcast.audio_url));
      a.onended = () => setPlaying(false);
      audioRef.current = a;
    }
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-violet-950/40 via-black/40 to-fuchsia-950/30 border-violet-400/20" data-testid="astro-radio">
      <div className="flex items-center gap-3 mb-2">
        <Radio className="w-5 h-5 text-violet-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-violet-300">// Cosmic Radio</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Today&apos;s cosmic broadcast</h2>
      <p className="text-slate-400 text-sm mt-1">A fresh ~4-minute audio show, written and voiced for your sign and today&apos;s sky. One free every day.</p>

      {!broadcast ? (
        <>
          {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-radio-err">{err}</div>}
          <button onClick={generate} disabled={busy} className="fl-btn mt-5 !bg-violet-500 hover:!bg-violet-400" data-testid="astro-radio-generate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Tuning in…" : "Generate today's show"}
          </button>
          <div className="mt-2 text-xs text-white/40 fl-mono">Takes ~30 seconds — Claude writes it, then OpenAI voices it.</div>
        </>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-3">
            <button onClick={play} className="fl-btn !bg-violet-500 hover:!bg-violet-400" data-testid="astro-radio-play">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Pause" : "Play"}
            </button>
            <button onClick={toggleBed} className="fl-btn ghost !py-2 !px-3 text-xs" data-testid="astro-radio-bed">
              {bedOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} Bed
            </button>
            <div className="text-xs text-white/40 fl-mono ml-auto">{broadcast.date}</div>
          </div>
          <details className="mt-4 group">
            <summary className="cursor-pointer text-xs fl-mono uppercase tracking-widest text-white/50 hover:text-violet-300">// Read the transcript</summary>
            <div className="mt-3 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap" data-testid="astro-radio-transcript">{broadcast.script}</div>
          </details>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// The Oracle — record → transcribe → answer → speak
// ────────────────────────────────────────────────────────────
function TheOracle({ personas, bedTrackUrl, onDone }) {
  const [persona, setPersona] = useState("grandma");
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reading, setReading] = useState(null);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const startRec = async () => {
    setErr(""); setReading(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { setErr("Recording was too short. Try again."); setProcessing(false); return; }
        setProcessing(true);
        const fd = new FormData();
        fd.append("audio", blob, "question.webm");
        fd.append("persona", persona);
        try {
          const { data } = await api.post("/astro/oracle", fd, { headers: { ...astroAuthHeaders(), "Content-Type": "multipart/form-data" }, timeout: 120000 });
          setReading(data); onDone?.();
        } catch (e) {
          setErr(e?.response?.data?.detail || "The oracle stayed silent — try again.");
        } finally { setProcessing(false); }
      };
      mr.start(); setRecording(true);
    } catch (e) {
      setErr("Microphone access denied. Enable it in your browser settings.");
    }
  };
  const stopRec = () => { try { mediaRef.current?.stop(); } catch (_) {} setRecording(false); };

  const playAnswer = () => {
    if (!reading?.audio_url) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (!audioRef.current) {
      const a = new Audio(resolveAudio(reading.audio_url));
      a.onended = () => setPlaying(false);
      audioRef.current = a;
    }
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-fuchsia-950/30 via-black/40 to-violet-950/40 border-fuchsia-400/20" data-testid="astro-oracle">
      <div className="flex items-center gap-3 mb-2">
        <MessageCircle className="w-5 h-5 text-fuchsia-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-fuchsia-300">// The Oracle</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Hold, speak, listen.</h2>
      <p className="text-slate-400 text-sm mt-1">Ask any question out loud. The oracle answers in the persona you pick.</p>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="astro-persona-picker">
        {(personas || []).map(p => (
          <button
            key={p.id}
            onClick={() => setPersona(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${persona === p.id ? "bg-fuchsia-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
            data-testid={`astro-persona-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {!recording && !processing && (
          <button onClick={startRec} className="fl-btn !bg-fuchsia-500 hover:!bg-fuchsia-400" data-testid="astro-oracle-start"><Mic className="w-4 h-4" /> Hold the mic — start</button>
        )}
        {recording && (
          <button onClick={stopRec} className="fl-btn !bg-red-500 hover:!bg-red-400 animate-pulse" data-testid="astro-oracle-stop"><Mic className="w-4 h-4" /> Stop &amp; ask</button>
        )}
        {processing && (
          <div className="flex items-center gap-2 text-fuchsia-300 fl-mono text-xs uppercase tracking-widest" data-testid="astro-oracle-processing">
            <Loader2 className="w-4 h-4 animate-spin" /> The stars are listening…
          </div>
        )}
      </div>

      {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-oracle-err">{err}</div>}

      {reading && (
        <div className="mt-5 space-y-3" data-testid="astro-oracle-result">
          <div className="text-white/60 text-xs fl-mono uppercase tracking-widest">// You asked</div>
          <div className="text-white/90 text-sm italic">&ldquo;{reading.question_text}&rdquo;</div>
          <div className="text-white/60 text-xs fl-mono uppercase tracking-widest mt-3">// {reading.persona_label} says</div>
          <div className="text-slate-200 text-sm leading-relaxed">{reading.answer_text}</div>
          <button onClick={playAnswer} className="fl-btn !bg-fuchsia-500 hover:!bg-fuchsia-400" data-testid="astro-oracle-play">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Pause" : "Hear the answer"}
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Readings archive
// ────────────────────────────────────────────────────────────
function ReadingsArchive() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/astro/readings?limit=25", { headers: astroAuthHeaders() });
      setRows(data.readings || []);
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); return () => { try { audioRef.current?.pause(); } catch (_) {} }; }, []);

  const play = (r) => {
    if (playingId === r.reading_id) { audioRef.current?.pause(); setPlayingId(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(resolveAudio(r.audio_url));
    a.onended = () => setPlayingId(null);
    a.play().then(() => { audioRef.current = a; setPlayingId(r.reading_id); }).catch(() => {});
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this reading? Cannot be undone.")) return;
    try { await api.delete(`/astro/readings/${r.reading_id}`, { headers: astroAuthHeaders() }); load(); } catch (_) {}
  };

  return (
    <div className="fl-card p-6 bg-white/[0.02] border-white/5" data-testid="astro-archive">
      <div className="flex items-center gap-3 mb-1">
        <Moon className="w-5 h-5 text-violet-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-violet-300">// Voicemails from the universe</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Your archive</h2>
      {loading ? (
        <div className="text-white/40 fl-mono text-xs mt-4">// Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-400 text-sm mt-3">No readings yet. Ask The Oracle a question to save your first voicemail.</div>
      ) : (
        <div className="mt-4 space-y-2">
          {rows.map(r => (
            <div key={r.reading_id} className="flex items-start gap-3 p-3 rounded-lg bg-black/40 border border-white/5" data-testid={`astro-reading-${r.reading_id}`}>
              <button onClick={() => play(r)} className="w-9 h-9 rounded-full bg-violet-500 hover:bg-violet-400 grid place-items-center text-white" data-testid={`astro-reading-play-${r.reading_id}`}>
                {playingId === r.reading_id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-white/90 text-sm italic truncate">&ldquo;{r.question_text}&rdquo;</div>
                <div className="text-white/50 text-xs mt-1">{r.persona_label} · {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => remove(r)} className="text-white/40 hover:text-red-400 p-1" title="Delete" data-testid={`astro-reading-delete-${r.reading_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Cosmic Alibi — blame the stars (Anti-Horoscope lives here too via tone=blunt)
// ────────────────────────────────────────────────────────────
function CosmicAlibi({ sign, onUsed }) {
  const [mood, setMood] = useState("");
  const [tone, setTone] = useState("funny");
  const [busy, setBusy] = useState(false);
  const [alibi, setAlibi] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const suggestions = ["impulsive shopping", "high anxiety", "couldn't sleep", "cried at a commercial", "ghosted my group chat", "ate cereal for dinner"];

  const generate = async () => {
    if (!mood.trim()) { setErr("Tell us what you want to blame the stars for."); return; }
    setErr(""); setBusy(true); setAlibi(null);
    try {
      const { data } = await api.post("/astro/alibi", { mood, tone }, { headers: astroAuthHeaders(), timeout: 60000 });
      setAlibi(data); onUsed?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Alibi generation failed."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    if (!alibi) return;
    const text = `${alibi.text}\n\n— via Fliboe Astro`;
    const shareUrl = `${window.location.origin}/api/share?app=astro&url=/astro&title=${encodeURIComponent("My cosmic alibi · " + sign)}&desc=${encodeURIComponent(alibi.text.slice(0, 200))}&badge=${encodeURIComponent("Astro")}`;
    try {
      if (navigator.share) await navigator.share({ text, title: "My cosmic alibi", url: shareUrl });
      else { await navigator.clipboard.writeText(`${text} ${shareUrl}`); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    } catch (_) {}
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-emerald-950/30 via-black/40 to-red-950/30 border-emerald-400/20" data-testid="astro-alibi">
      <div className="flex items-center gap-3 mb-2">
        <Flame className="w-5 h-5 text-emerald-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300">// The Cosmic Alibi</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Blame the stars. Publicly.</h2>
      <p className="text-slate-400 text-sm mt-1">Bad decisions today? Tell us what happened — we&apos;ll write the cosmic excuse. Screenshot-ready.</p>

      <div className="mt-4">
        <input
          className="fl-input w-full"
          value={mood}
          onChange={e => setMood(e.target.value)}
          placeholder="e.g. impulsive shopping, high anxiety, ghosted my group chat"
          data-testid="astro-alibi-mood"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map(s => (
            <button key={s} onClick={() => setMood(s)} className="text-[10px] fl-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-white/60 hover:bg-emerald-500/20 hover:text-emerald-200" data-testid={`astro-alibi-suggest-${s.replace(/\s+/g, "-")}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Tone</span>
        {[
          { id: "funny", label: "Funny" },
          { id: "blunt", label: "Anti-Horoscope (blunt)" },
          { id: "mystical", label: "Mystical" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTone(t.id)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${tone === t.id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
            data-testid={`astro-alibi-tone-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-alibi-err">{err}</div>}

      <button onClick={generate} disabled={busy} className="fl-btn mt-4 !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="astro-alibi-generate">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {busy ? "Consulting the cosmos…" : "Get my alibi"}
      </button>

      {alibi && (
        <div className="mt-5 p-4 rounded-xl bg-black/60 border border-emerald-400/20" data-testid="astro-alibi-result">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300">// {sign} · {alibi.tone}</div>
          <p className="text-slate-100 text-[15px] leading-relaxed mt-2">{alibi.text}</p>
          <button onClick={share} className="fl-btn ghost !py-2 !px-3 text-xs mt-3" data-testid="astro-alibi-share">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />} {copied ? "Copied" : "Share"}
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// "Is it just me?" — real-time sign pulse
// ────────────────────────────────────────────────────────────
function IsItJustMe({ sign }) {
  const [vibes, setVibes] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(null);
  const [err, setErr] = useState("");

  const loadPulse = async () => {
    try {
      const { data } = await api.get(`/astro/pulse/sign/${sign}`);
      setPulse(data);
    } catch (_) {}
  };

  useEffect(() => {
    api.get("/astro/pulse/vibes").then(({ data }) => setVibes(data.vibes || [])).catch(() => {});
    loadPulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sign]);

  const vote = async (v) => {
    setErr(""); setBusy(true);
    try {
      await api.post("/astro/pulse/vote", { vibe: v }, { headers: astroAuthHeaders() });
      setSelected(v);
      loadPulse();
    } catch (e) { setErr(e?.response?.data?.detail || "Vote failed."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    if (!pulse?.top || !pulse.total) return;
    const text = `It's not just me. ${pulse.top.pct}% of ${sign}s are feeling ${pulse.top.vibe} today. — via Fliboe Astro`;
    const shareUrl = `${window.location.origin}/api/share?app=astro&url=/astro&title=${encodeURIComponent(`${pulse.top.pct}% of ${sign}s feel ${pulse.top.vibe} today`)}&desc=${encodeURIComponent(`Live sign pulse from Fliboe Astro · ${pulse.total} votes so far`)}&badge=${encodeURIComponent("Pulse")}`;
    try {
      if (navigator.share) await navigator.share({ text, title: "Sign pulse", url: shareUrl });
      else await navigator.clipboard.writeText(`${text} ${shareUrl}`);
    } catch (_) {}
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-emerald-950/30 via-black/40 to-cyan-950/30 border-emerald-400/20" data-testid="astro-pulse">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-5 h-5 text-emerald-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300">// Is it just me?</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white capitalize">How are {sign}s feeling today?</h2>
      <p className="text-slate-400 text-sm mt-1">Tap the word that fits. See how the rest of your sign feels right now.</p>

      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="astro-pulse-vibes">
        {vibes.map(v => (
          <button
            key={v}
            onClick={() => vote(v)}
            disabled={busy}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${selected === v ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-200"}`}
            data-testid={`astro-pulse-vibe-${v}`}
          >
            {v}
          </button>
        ))}
      </div>

      {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-pulse-err">{err}</div>}

      {pulse && pulse.total > 0 ? (
        <div className="mt-5" data-testid="astro-pulse-result">
          {pulse.top && (
            <div className="p-4 rounded-xl bg-black/60 border border-emerald-400/20">
              <div className="text-white text-lg leading-snug">
                <span className="text-emerald-300 font-bold">{pulse.top.pct}%</span> of <span className="capitalize">{sign}s</span> are feeling <span className="text-emerald-300 font-bold">{pulse.top.vibe}</span> today.
              </div>
              <div className="text-white/50 text-xs fl-mono mt-2">// {pulse.total} vote{pulse.total === 1 ? "" : "s"} · resets daily</div>
              <button onClick={share} className="fl-btn ghost !py-2 !px-3 text-xs mt-3" data-testid="astro-pulse-share">
                <Share2 className="w-3.5 h-3.5" /> Share this pulse
              </button>
            </div>
          )}
          {pulse.breakdown && pulse.breakdown.length > 1 && (
            <div className="mt-3 space-y-1">
              {pulse.breakdown.slice(0, 5).map(b => (
                <div key={b.vibe} className="flex items-center gap-2 text-xs">
                  <span className="text-white/70 capitalize w-24">{b.vibe}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-emerald-400/70" style={{ width: `${b.pct}%` }} />
                  </div>
                  <span className="text-white/60 fl-mono">{b.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 text-slate-400 text-xs fl-mono">// Be the first to vote today.</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Micro-Synastry — sync with a friend's code
// ────────────────────────────────────────────────────────────
function MicroSynastry({ sign, onUsed }) {
  const [myCode, setMyCode] = useState("");
  const [friendCode, setFriendCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/astro/synastry/code", { headers: astroAuthHeaders() })
      .then(({ data }) => setMyCode(data.code))
      .catch(() => {});
  }, []);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(myCode); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch (_) {}
  };

  const run = async () => {
    if (!friendCode.trim()) { setErr("Enter your friend's Astro code."); return; }
    setErr(""); setBusy(true); setForecast(null);
    try {
      const { data } = await api.post("/astro/synastry", { friend_code: friendCode.trim().toUpperCase(), label: label || null }, { headers: astroAuthHeaders(), timeout: 60000 });
      setForecast(data); onUsed?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Synastry failed."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    if (!forecast) return;
    const text = `${forecast.text}\n\n— via Fliboe Astro`;
    const shareUrl = `${window.location.origin}/api/share?app=astro&url=/astro&title=${encodeURIComponent(`${forecast.my_sign} × ${forecast.friend_sign} today`)}&desc=${encodeURIComponent(forecast.text.slice(0, 200))}&badge=${encodeURIComponent("Synastry")}`;
    try {
      if (navigator.share) await navigator.share({ text, title: "Our interaction forecast", url: shareUrl });
      else await navigator.clipboard.writeText(`${text} ${shareUrl}`);
    } catch (_) {}
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-pink-950/30 via-black/40 to-violet-950/40 border-pink-400/20" data-testid="astro-synastry">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-5 h-5 text-pink-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">// Micro-Synastry</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Sync with someone.</h2>
      <p className="text-slate-400 text-sm mt-1">Get a 70-100 word daily interaction forecast between the two of you. Share your code, enter theirs.</p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div className="p-3 rounded-xl bg-black/60 border border-pink-400/20">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">// Your code</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="fl-display text-2xl font-black text-white tracking-widest" data-testid="astro-syn-mycode">{myCode || "…"}</div>
            <button onClick={copyCode} disabled={!myCode} className="fl-btn ghost !py-1 !px-2 text-xs ml-auto" data-testid="astro-syn-copy">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-black/60 border border-pink-400/20">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">// Their code</div>
          <input
            className="fl-input w-full mt-1 uppercase tracking-widest fl-display text-lg"
            value={friendCode}
            onChange={e => setFriendCode(e.target.value.toUpperCase())}
            placeholder="6-char code"
            maxLength={8}
            data-testid="astro-syn-friendcode"
          />
          <input
            className="fl-input w-full mt-2 text-sm"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Nickname (roommate, partner…)"
            maxLength={40}
            data-testid="astro-syn-label"
          />
        </div>
      </div>

      {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-syn-err">{err}</div>}

      <button onClick={run} disabled={busy || !friendCode} className="fl-btn mt-4 !bg-pink-500 hover:!bg-pink-400" data-testid="astro-syn-run">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Aligning charts…" : "Get today's forecast"}
      </button>

      {forecast && (
        <div className="mt-5 p-4 rounded-xl bg-black/60 border border-pink-400/20" data-testid="astro-syn-result">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-pink-300">// {forecast.my_sign} × {forecast.friend_sign}{forecast.label ? ` · ${forecast.label}` : ""}</div>
          <p className="text-slate-100 text-[15px] leading-relaxed mt-2">{forecast.text}</p>
          <button onClick={share} className="fl-btn ghost !py-2 !px-3 text-xs mt-3" data-testid="astro-syn-share">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// PulseAlerts — "It's not just you" banner + bell (polls /astro/alerts)
// ────────────────────────────────────────────────────────────
function PulseAlerts({ sign }) {
  const [alerts, setAlerts] = useState([]);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/astro/alerts", { headers: astroAuthHeaders() });
      setAlerts(data.alerts || []);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sign]);

  const dismiss = async (a) => {
    setAlerts(prev => prev.filter(x => x.alert_id !== a.alert_id));
    try { await api.post(`/astro/alerts/${a.alert_id}/read`, {}, { headers: astroAuthHeaders() }); } catch (_) {}
  };

  const shareAlert = async (a) => {
    const text = `It's not just me. ${a.pct}% of ${a.sign}s are feeling ${a.vibe} today. — via Fliboe Astro`;
    const shareUrl = `${window.location.origin}/api/share?app=astro&url=/astro&title=${encodeURIComponent(`${a.pct}% of ${a.sign}s feel ${a.vibe} right now`)}&desc=${encodeURIComponent("Live sign pulse alert from Fliboe Astro")}&badge=${encodeURIComponent("Alert")}`;
    try {
      if (navigator.share) await navigator.share({ text, title: "Cosmic pulse alert", url: shareUrl });
      else await navigator.clipboard.writeText(`${text} ${shareUrl}`);
    } catch (_) {}
  };

  if (!alerts.length) return null;
  return (
    <div className="space-y-2" data-testid="astro-alerts">
      {alerts.map(a => (
        <div key={a.alert_id} className="fl-card p-4 flex items-start gap-3 bg-gradient-to-r from-emerald-500/15 via-black/40 to-fuchsia-500/10 border-emerald-400/30 animate-in fade-in" data-testid={`astro-alert-${a.alert_id}`}>
          <Bell className="w-5 h-5 text-emerald-300 mt-0.5" />
          <div className="flex-1">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300">// It&apos;s not just you</div>
            <div className="text-white text-[15px] leading-snug mt-1">
              <span className="text-emerald-300 font-bold">{a.pct}%</span> of <span className="capitalize">{a.sign}s</span> are feeling <span className="text-emerald-300 font-bold">{a.vibe}</span> right now.
            </div>
            <div className="mt-2 flex gap-2">
              <button onClick={() => shareAlert(a)} className="text-xs fl-mono uppercase tracking-widest text-emerald-200 hover:text-white" data-testid={`astro-alert-share-${a.alert_id}`}>Share</button>
              <button onClick={() => dismiss(a)} className="text-xs fl-mono uppercase tracking-widest text-white/50 hover:text-white ml-auto" data-testid={`astro-alert-dismiss-${a.alert_id}`}>Dismiss</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// BelieverShow — weekly 10-min personalized cosmic show (Believer only)
// ────────────────────────────────────────────────────────────
function BelieverShow({ isBeliever }) {
  const [show, setShow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/astro/broadcast/believer/this-week", { headers: astroAuthHeaders() })
      .then(({ data }) => setShow(data?.audio_url ? data : null))
      .catch(() => {});
    return () => { try { audioRef.current?.pause(); } catch (_) {} };
  }, []);

  const generate = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/astro/broadcast/believer/generate", {}, { headers: astroAuthHeaders(), timeout: 300000 });
      setShow(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "The cosmos is taking its time — try again in a minute." : "Believer show generation failed."));
    } finally { setBusy(false); }
  };

  const play = () => {
    if (!show?.audio_url) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (!audioRef.current) {
      const a = new Audio(resolveAudio(show.audio_url));
      a.onended = () => setPlaying(false);
      audioRef.current = a;
    }
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <div className="fl-card p-6 bg-gradient-to-br from-indigo-950/40 via-black/60 to-violet-950/50 border-indigo-400/30 relative overflow-hidden" data-testid="astro-believer">
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" aria-hidden />
      <div className="flex items-center gap-3 mb-2">
        <Crown className="w-5 h-5 text-indigo-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-indigo-300">// Believer Show · Weekly</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Your 10-minute weekly cosmic show.</h2>
      <p className="text-slate-400 text-sm mt-1">Deeper than the daily broadcast — ~1000 words, three timed windows to watch, and a closing meditation. One fresh show every calendar week.</p>

      {!isBeliever ? (
        <div className="mt-5 p-4 rounded-xl bg-black/60 border border-indigo-400/20" data-testid="astro-believer-upsell">
          <div className="text-white text-sm">Astro Believer plan required — <span className="fl-mono text-indigo-300">$9.99/month</span>.</div>
          <div className="text-slate-400 text-xs mt-1">Unlocks the weekly show plus everything in Astro Pro.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/payments/checkout", { package_id: "astro_believer_1mo", origin_url: window.location.origin }, { headers: astroAuthHeaders() });
                  if (data?.url) window.location.href = data.url;
                } catch (e) { alert(e?.response?.data?.detail || "Could not start checkout — sign in first."); }
              }}
              className="fl-btn !bg-indigo-500 hover:!bg-indigo-400"
              data-testid="astro-believer-upgrade"
            >
              <Crown className="w-4 h-4" /> Upgrade to Believer · $9.99
            </button>
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/payments/checkout", { package_id: "astro_pro_1mo", origin_url: window.location.origin }, { headers: astroAuthHeaders() });
                  if (data?.url) window.location.href = data.url;
                } catch (e) { alert(e?.response?.data?.detail || "Could not start checkout — sign in first."); }
              }}
              className="fl-btn ghost"
              data-testid="astro-pro-upgrade"
            >
              Astro Pro · $4.99
            </button>
          </div>
        </div>
      ) : !show ? (
        <>
          {err && <div className="text-red-300 text-sm mt-3" data-testid="astro-believer-err">{err}</div>}
          <button onClick={generate} disabled={busy} className="fl-btn mt-5 !bg-indigo-500 hover:!bg-indigo-400" data-testid="astro-believer-generate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Writing your show…" : "Generate this week's show"}
          </button>
          <div className="mt-2 text-xs text-white/40 fl-mono">Takes ~60-90 seconds. Cached for the whole ISO week.</div>
        </>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-3">
            <button onClick={play} className="fl-btn !bg-indigo-500 hover:!bg-indigo-400" data-testid="astro-believer-play">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? "Pause" : "Play show"}
            </button>
            <div className="text-xs text-white/40 fl-mono ml-auto">{show.week}</div>
          </div>
          <details className="mt-4 group">
            <summary className="cursor-pointer text-xs fl-mono uppercase tracking-widest text-white/50 hover:text-indigo-300">// Read the transcript</summary>
            <div className="mt-3 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap" data-testid="astro-believer-transcript">{show.script}</div>
          </details>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// EmbedSnippet — one-line iframe for creators to embed sign pulse
// ────────────────────────────────────────────────────────────
const ASTRO_ALL_SIGNS = ["aries","taurus","gemini","cancer","leo","virgo","libra","scorpio","sagittarius","capricorn","aquarius","pisces"];

function EmbedSnippet({ defaultSign }) {
  const [sign, setSign] = useState(defaultSign || "aries");
  const [theme, setTheme] = useState("dark");
  const [snippet, setSnippet] = useState("");
  const [preview, setPreview] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/astro/embed-snippet?sign=${sign}&theme=${theme}`);
      setSnippet(data.snippet); setPreview(data.preview_url);
    } catch (_) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sign, theme]);

  const copyIt = async () => {
    try { await navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };

  return (
    <div className="fl-card p-6 bg-white/[0.02] border-white/5" data-testid="astro-embed">
      <div className="flex items-center gap-3 mb-2">
        <Code2 className="w-5 h-5 text-cyan-300" />
        <div className="fl-mono text-[10px] uppercase tracking-widest text-cyan-300">// Embed on your site</div>
      </div>
      <h2 className="fl-display text-2xl font-black text-white">Give creators the pulse.</h2>
      <p className="text-slate-400 text-sm mt-1">One-line iframe. Drops today&apos;s cosmic vibe for any sign on any blog, newsletter, or landing page. Auto-refreshes every 5 minutes.</p>

      <div className="mt-4 flex flex-wrap gap-3 items-end">
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Sign</span>
          <select value={sign} onChange={e => setSign(e.target.value)} className="fl-input mt-1 capitalize" data-testid="astro-embed-sign">
            {ASTRO_ALL_SIGNS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Theme</span>
          <select value={theme} onChange={e => setTheme(e.target.value)} className="fl-input mt-1" data-testid="astro-embed-theme">
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </label>
      </div>

      {preview && (
        <div className="mt-4 rounded-xl overflow-hidden border border-white/10 max-w-[360px]" data-testid="astro-embed-preview">
          <iframe src={preview} title="Fliboe Astro widget preview" width="340" height="150" style={{ border: 0, display: "block" }} loading="lazy" />
        </div>
      )}

      <div className="mt-4">
        <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">Copy this snippet</div>
        <textarea readOnly value={snippet} rows={3} className="fl-input w-full font-mono text-xs" data-testid="astro-embed-snippet" onFocus={e => e.target.select()} />
        <button onClick={copyIt} className="fl-btn mt-3 !bg-cyan-500 !text-black hover:!bg-cyan-400" data-testid="astro-embed-copy">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy embed"}
        </button>
      </div>
    </div>
  );
}

